import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';

import {
  BarChart,
  ResponsiveContainer,
  YAxis,
  XAxis,
  Tooltip,
  Bar,
  Legend,
} from 'recharts';

import { Checkbox } from 'pretty-checkbox-react';
import CHARTCOLORS from '../../helpers/chartColors';
import formatValue from '../../helpers/chartFormatter';
import EnergyTracerMultiLineChart, { createDownloadHeadersAndKeys, SELECTION_KEY, SELECTION_BASELINE_KEY, SELECTION_BASELINE2_KEY } from '../../components/EnergyTracerMultiLineChart';
import GraphActionContainer from '../../components/chartElements/GraphActionContainer';
import ChartToggle from '../../components/chartElements/Actions/ChartToggle';
import ChartDownload from '../../components/chartElements/Actions/ChartDownload';
import { SUB_TYPES } from '../../helpers/utility-types';
import { ET_FRONTEND_CONVERSIONS } from '../../helpers/consumptionUnits';

import 'pretty-checkbox';


const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

function WaterUtilityTrends(props) {
  const [deferredProps, setDeferredProps] = useState({ ...props });
  useEffect(() => {
    if (props.open) setDeferredProps({ ...props });
  }, [props]);
  const {
    dashboardData,
    baselineData,
    baseline2Data,
    showBaselineData,
    showBaseline2Data,
    organization,
    activeIds,
    activeKeys,
    unitSelected,
  } = deferredProps;


  const [availableSubTypes, setAvailableSubTypes] = useState([]);
  const [selectedSubTypes, setSelectedSubTypes] = useState([]);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [subCostBarData, setSubCostBarData] = useState([]);
  const [subUsageBarData, setSubUsageBarData] = useState([]);
  const [costBarData, setCostBarData] = useState([]);
  const [costBarKeys, setCostBarKeys] = useState([])

  const [costSummary, setCostSummary] = useState(true);
  const [usageSummary, setUsageSummary] = useState(true);



  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);
  const [chartSubBarDownloadHeaders, setChartSubBarDownloadHeaders] = useState([]);
  const [chartSubBarDownloadKeys, setChartSubBarDownloadKeys] = useState([]);
  const [chartCostBarDownloadHeaders, setChartCostBarDownloadHeaders] = useState([]);
  const [chartCostBarDownloadKeys, setChartCostBarDownloadKeys] = useState([])


  useEffect(() => {
    let found_sub_types = [];
    let found_usage_sub_types = [];
    Object.keys(dashboardData['building_usage']).forEach((bid) => {
      if (dashboardData['building_usage'][bid]['water']) {
        Object.keys(dashboardData['building_usage'][bid]['water']['sub_costs']).forEach((sub_type) => {
          if (!found_sub_types.includes(sub_type)) {
            found_sub_types.push(sub_type)
          }
        })
        Object.keys(dashboardData['building_usage'][bid]['water']['sub_usages']).forEach((sub_type) => {
          if (!found_sub_types.includes(sub_type)) {
            found_usage_sub_types.push(sub_type)
          }
        })
      }
    })
    setAvailableSubTypes(found_sub_types);
    setSelectedSubTypes(found_sub_types);
  }, [dashboardData])

  const changeCheck = (check) => {
    let new_checks = [...selectedSubTypes];
    if (new_checks.includes(check)) {
      new_checks = new_checks.filter(function (item) {
        return item !== check;
      });
    } else {
      new_checks.push(check);
    }
    new_checks = new_checks.sort()
    setSelectedSubTypes(new_checks);
  };


  const convertToChartForm = useCallback(
    (
      dashboardData,
      baselineData,
      baseline2Data,
      activeIds,
      activeKeys,
      selectedSubTypes,
      unitSelected,
      sub_option,
    ) => {
      const hasBaseline = Object.keys(baselineData).length > 0;
      const hasBaseline2 = Object.keys(baseline2Data).length > 0;
      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
          [SELECTION_KEY]: 0,
          [SELECTION_BASELINE_KEY]: 0,
          [SELECTION_BASELINE2_KEY]: 0,
        };
      });
      let factor = 1;
      if (
        sub_option === 'sub_usages' &&
        Object.keys(ET_FRONTEND_CONVERSIONS['water']).includes(
          unitSelected
        )
      ) {
        factor = ET_FRONTEND_CONVERSIONS['water'][unitSelected];
      }

      data.forEach((entry, index) => {
        activeIds.forEach((id, index2) => {
          let key = activeKeys[index2];

          let val = getVal(dashboardData, id, selectedSubTypes, sub_option, index) * factor
          entry[key] = val
          entry[SELECTION_KEY] += val
          if (hasBaseline) {
            let baseline_val = getVal(baselineData, id, selectedSubTypes, sub_option, index) * factor
            entry[key + ' Baseline'] = baseline_val;
            entry[SELECTION_BASELINE_KEY] += baseline_val
          }
          if (hasBaseline2) {
            let baseline2_val = getVal(baseline2Data, id, selectedSubTypes, sub_option, index) * factor
            entry[key + ' Baseline 2'] = baseline2_val
            entry[SELECTION_BASELINE2_KEY] += baseline2_val
          }
        });
      });
      return data;
    },
    []
  );

  const convertBarCosts = useCallback(
    (dashboardData, selectedSubTypes, activeIds) => {
      let cost_bar_keys = [];
      let local_ids = [...activeIds];

      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
        };
      });
      data.forEach((entry, index) => {
        local_ids.forEach((id) => {
          let sub_usage_costs = {};
          if (
            dashboardData['building_usage'][id] &&
            dashboardData['building_usage'][id]['water'] &&
            dashboardData['building_usage'][id]['water']['sub_usage_costs']
          ) {
            sub_usage_costs =
              dashboardData['building_usage'][id]['water']['sub_usage_costs'];
          }
          Object.keys(sub_usage_costs).forEach((suc) => {
            let usage = suc.split(":::")[0]
            let cost = suc.split(":::")[1]
            if (selectedSubTypes.includes(usage)) {
              if (!cost_bar_keys.includes(cost) && sub_usage_costs[suc][index] !== 0) {
                cost_bar_keys.push(cost);
              }
              if (entry[cost]) {
                entry[cost] += sub_usage_costs[suc][index];
              } else {
                entry[cost] = sub_usage_costs[suc][index];
              }
            }
          });
        });
      });
      setCostBarKeys(cost_bar_keys);
      return data;
    },
    []
  );


  const convertSubBars = useCallback(
    (dashboardData, activeIds, selectedSubTypes, unitSelected, sub_option) => {
      let local_ids = [...activeIds];

      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
        };
      });
      let factor = 1;
      if (
        sub_option === 'sub_usages' &&
        Object.keys(ET_FRONTEND_CONVERSIONS['water']).includes(
          unitSelected
        )
      ) {
        factor = ET_FRONTEND_CONVERSIONS['water'][unitSelected];
      }
      data.forEach((entry, index) => {
        local_ids.forEach((id) => {
          let sub_types = {};
          if (
            dashboardData['building_usage'][id] &&
            dashboardData['building_usage'][id]['water'] &&
            dashboardData['building_usage'][id]['water'][sub_option]
          ) {
            sub_types =
              dashboardData['building_usage'][id]['water'][sub_option];
          }
          Object.keys(sub_types).forEach((sub_key) => {
            let parsed_key = Object.keys(SUB_TYPES['water']).includes(sub_key) ? SUB_TYPES['water'][sub_key] : sub_key
            if (selectedSubTypes.includes(sub_key)) {
              if (entry[parsed_key]) {
                entry[parsed_key] += sub_types[sub_key][index] * factor;
              } else {
                entry[parsed_key] = sub_types[sub_key][index] * factor;
              }

            }
          });
        });
      });
      return data;
    },
    []
  );


  useEffect(() => {
    setCostData(convertToChartForm(dashboardData, baselineData, baseline2Data, activeIds, activeKeys, selectedSubTypes, unitSelected, 'sub_costs'))
    setSubCostBarData(convertSubBars(dashboardData, activeIds, selectedSubTypes, unitSelected, 'sub_costs'));

    setCostBarData(convertBarCosts(dashboardData, selectedSubTypes, activeIds));


    setUsageData(convertToChartForm(dashboardData, baselineData, baseline2Data, activeIds, activeKeys, selectedSubTypes, unitSelected, 'sub_usages'))
    setSubUsageBarData(convertSubBars(dashboardData, activeIds, selectedSubTypes, unitSelected, 'sub_usages'));
  }, [
    dashboardData, baselineData, baseline2Data, activeIds, activeKeys, selectedSubTypes, unitSelected, convertToChartForm, convertBarCosts, convertSubBars
  ])


  useEffect(() => {
    const hasBaseline = Object.keys(baselineData).length > 0;
    const hasBaseline2 = Object.keys(baseline2Data).length > 0;

    createDownloadHeadersAndKeys(
      activeKeys, setChartDownloadHeaders, setChartDownloadKeys, hasBaseline, hasBaseline2
    )
  }, [activeKeys, baselineData, baseline2Data]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    costBarKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartCostBarDownloadHeaders(headers);
    setChartCostBarDownloadKeys(keys);
  }, [costBarKeys]);


  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    selectedSubTypes.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartSubBarDownloadHeaders(headers);
    setChartSubBarDownloadKeys(keys);
  }, [selectedSubTypes]);



  return <>
    <Form style={{ marginTop: '25px', marginBottom: '10px' }}>
      <Form.Label style={{ display: 'inline-block', position: 'relative', 'bottom': '3px' }}>Cost Sub-Types:</Form.Label>
      <Form.Row style={{ marginLeft: '0.5em', display: 'inline-block' }}>
        {availableSubTypes.sort().map((sub_type) => (
          <Checkbox
            style={{
              fontSize: '1.25em',
              marginBottom: '0.5em',
            }}
            color={'primary-o'}
            onChange={() => {
              changeCheck(sub_type);
            }}
            checked={selectedSubTypes.includes(sub_type)}
            key={sub_type}
          >
            <div>
              <div style={{
                fontSize: '1rem',
                position: 'relative',
                top: '-0.25em',
              }}> {Object.keys(SUB_TYPES['water']).includes(sub_type) ? SUB_TYPES['water'][sub_type] : sub_type}</div>
            </div>
          </Checkbox>
        ))}
      </Form.Row>
    </Form>
    <h5>{'Total Cost ($)'} </h5>
    <div style={{ ...row, marginBottom: '1em' }}>
      <EnergyTracerMultiLineChart
        data={!costData ? [] : costData}
        unit={'$'}
        keys={activeKeys}
        baseline={Object.keys(baselineData).length > 0 && showBaselineData}
        baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
        showSummary={costSummary}
      />
      <GraphActionContainer>
        <ChartToggle
          onClick={() => {
            setCostSummary((prev) => !prev);
          }}
          state={costSummary ? 1 : 0}
          title={'Toggle Selection Total'}
          variant={3}
        />
        <ChartDownload
          organization_id={organization.id}
          data={costData}
          headers={chartDownloadHeaders}
          keys={chartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>



    <h5>{'Cost Breakdown ($)'}</h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <ResponsiveContainer width={'100%'} height={300} debounce={300}>
        <BarChart data={costBarData}>
          <YAxis
            type='number'
            tickFormatter={(tick) => {
              return tick.toLocaleString();
            }}
          />
          <XAxis type='category' dataKey='name' />
          <Tooltip />
          <Legend
            wrapperStyle={{ bottom: '0px', position: 'relative' }}
          />
          {costBarKeys.map((cost, idx) => (
            <Bar
              dataKey={cost}
              fill={CHARTCOLORS[idx]}
              formatter={(value) => formatValue(value, '$')}
              stackId='a'
              key={cost + '_' + idx}
            ></Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <GraphActionContainer customStyle={{ marginTop: '1em' }}>
        <ChartDownload
          organization_id={organization.id}
          data={costBarData}
          headers={chartCostBarDownloadHeaders}
          keys={chartCostBarDownloadKeys}
          chart_type={'bar'}
        />
      </GraphActionContainer>
    </div>


    <h5>{'Sub-Type Cost Breakdown ($)'}</h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <ResponsiveContainer width={'100%'} height={300} debounce={300}>
        <BarChart data={subCostBarData}>
          <YAxis
            type='number'
            tickFormatter={(tick) => {
              return tick.toLocaleString();
            }}
          />
          <XAxis type='category' dataKey='name' />
          <Tooltip />
          <Legend
            wrapperStyle={{ bottom: '0px', position: 'relative' }}
          />
          {selectedSubTypes.map((cost, idx) => (
            <Bar
              dataKey={Object.keys(SUB_TYPES['water']).includes(cost) ? SUB_TYPES['water'][cost] : cost}
              fill={CHARTCOLORS[idx]}
              formatter={(value) => formatValue(value, '$')}
              stackId='a'
              key={cost + '_' + idx}
            ></Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <GraphActionContainer customStyle={{ marginTop: '1em' }}>
        <ChartDownload
          organization_id={organization.id}
          data={subCostBarData}
          headers={chartSubBarDownloadHeaders}
          keys={chartSubBarDownloadKeys}
          chart_type={'bar'}
        />
      </GraphActionContainer>
    </div>

    <h5>{'Total Usage (' + unitSelected + ")"} </h5>
    <div style={{ ...row, marginBottom: '1em' }}>
      <EnergyTracerMultiLineChart
        data={!usageData ? [] : usageData}
        unit={''}
        keys={activeKeys}
        baseline={Object.keys(baselineData).length > 0 && showBaselineData}
        baseline2={Object.keys(baseline2Data).length > 0 && showBaseline2Data}
        showSummary={usageSummary}
      />
      <GraphActionContainer>
        <ChartToggle
          onClick={() => {
            setUsageSummary((prev) => !prev);
          }}
          state={usageSummary ? 1 : 0}
          title={'Toggle Selection Total'}
          variant={3}
        />
        <ChartDownload
          organization_id={organization.id}
          data={usageData}
          headers={chartDownloadHeaders}
          keys={chartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>

    <h5>{'Sub-Type Usage Breakdown'}</h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <ResponsiveContainer width={'100%'} height={300} debounce={300}>
        <BarChart data={subUsageBarData}>
          <YAxis
            type='number'
            tickFormatter={(tick) => {
              return tick.toLocaleString();
            }}
          />
          <XAxis type='category' dataKey='name' />
          <Tooltip />
          <Legend
            wrapperStyle={{ bottom: '0px', position: 'relative' }}
          />
          {selectedSubTypes.map((usage, idx) => (
            <Bar
              dataKey={Object.keys(SUB_TYPES['water']).includes(usage) ? SUB_TYPES['water'][usage] : usage}
              fill={CHARTCOLORS[idx]}
              formatter={(value) => formatValue(value)}
              stackId='a'
              key={usage + '_' + idx}
            ></Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <GraphActionContainer customStyle={{ marginTop: '1em' }}>
        <ChartDownload
          organization_id={organization.id}
          data={subUsageBarData}
          headers={chartSubBarDownloadHeaders}
          keys={chartSubBarDownloadKeys}
          chart_type={'bar'}
        />
      </GraphActionContainer>
    </div>

  </>

}

WaterUtilityTrends.propTypes = {
  baselineData: PropTypes.object,
  baseline2Data: PropTypes.object,

};

WaterUtilityTrends.defaultProps = {
  baselineData: {},
  baseline2Data: {},
};

export default WaterUtilityTrends;

function getVal(dashboardData, bid, selectedSubTypes, sub_option, index) {
  let val = 0
  if (dashboardData['building_usage'][bid] && dashboardData['building_usage'][bid]['water']) {
    Object.keys(dashboardData['building_usage'][bid]['water'][sub_option]).forEach((sub) => {
      if (selectedSubTypes.includes(sub)) {
        val += dashboardData['building_usage'][bid]['water'][sub_option][sub][index]
      }
    })
  }
  return val
}